import React, { useState, useEffect, useRef } from 'react';
import '../../shared/sheet-animations.css';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '../icons/FinanceIcons';
import { datePresetLabels } from '../data/mockData';
import useWindowSize from '../../new-service/useWindowSize';

// Format date as "1 Mar 2026"
function formatDateInput(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Parse typed date string like "1 Mar 2026", "01/03/2026", "1/3/2026", "2026-03-01"
function parseDateInput(str) {
  if (!str || !str.trim()) return null;
  const s = str.trim();

  // Try "1 Mar 2026" or "1 March 2026"
  const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
  const namedMatch = s.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (namedMatch) {
    const day = parseInt(namedMatch[1]);
    const monthStr = namedMatch[2].toLowerCase().slice(0, 3);
    const year = parseInt(namedMatch[3]);
    const monthIdx = monthNames.indexOf(monthStr);
    if (monthIdx !== -1 && day >= 1 && day <= 31) {
      return new Date(year, monthIdx, day);
    }
  }

  // Try "dd/mm/yyyy" or "d/m/yyyy"
  const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const day = parseInt(slashMatch[1]);
    const month = parseInt(slashMatch[2]) - 1;
    const year = parseInt(slashMatch[3]);
    if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
      return new Date(year, month, day);
    }
  }

  // Try "yyyy-mm-dd"
  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1]);
    const month = parseInt(isoMatch[2]) - 1;
    const day = parseInt(isoMatch[3]);
    if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
      return new Date(year, month, day);
    }
  }

  return null;
}

// This picker opens INSIDE modals that now sit above the app chrome (see
// ReportsView MODAL_PANEL_Z). At its old 200/201 it rendered behind them. It is
// a popover, so it belongs above whatever opened it in every caller.
const PICKER_BACKDROP_Z = 100002;
const PICKER_PANEL_Z = 100003;

// Date Range Picker Component
const DateRangePicker = ({
  isOpen,
  onClose,
  startDate,
  endDate,
  selectedPreset,
  onPresetChange,
  onApply,
  align = 'left',
  openDirection = 'down'
}) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [viewMonth, setViewMonth] = useState(startDate || new Date());
  const panelRef = useRef(null);
  const [resolvedDirection, setResolvedDirection] = useState(openDirection);
  const [localStart, setLocalStart] = useState(startDate);
  const [localEnd, setLocalEnd] = useState(endDate);
  const [selecting, setSelecting] = useState('start'); // 'start' or 'end'
  const [startInput, setStartInput] = useState(startDate ? formatDateInput(startDate) : '');
  const [endInput, setEndInput] = useState(endDate ? formatDateInput(endDate) : '');
  const prevOpenRef = useRef(false);

  // Sync local state with props when picker opens
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setLocalStart(startDate);
      setLocalEnd(endDate);
      setStartInput(startDate ? formatDateInput(startDate) : '');
      setEndInput(endDate ? formatDateInput(endDate) : '');
      setSelecting('start');
      if (startDate) {
        setViewMonth(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
      }
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, startDate, endDate]);

  // Callers declare a preferred direction, but the preference is never checked
  // against the room actually available. A ~480px panel opened upward from a
  // control low in a vertically-centred modal (Reports > Generate) overshoots
  // the top of the viewport and lands on top of the app header — it reads as a
  // stacking bug but the z-index is fine; nothing ever measured. Measure the
  // anchor, honour the preference when it fits, otherwise flip to whichever
  // side has more room.
  useEffect(() => {
    if (!isOpen || isMobile) {
      setResolvedDirection(openDirection);
      return;
    }
    const anchorEl = panelRef.current && panelRef.current.parentElement;
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const needed = ((panelRef.current && panelRef.current.offsetHeight) || 0) + 8; // panel + its offset
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const fits = openDirection === 'up' ? spaceAbove >= needed : spaceBelow >= needed;
    setResolvedDirection(fits ? openDirection : (spaceAbove > spaceBelow ? 'up' : 'down'));
  }, [isOpen, isMobile, openDirection, width]);

  if (!isOpen) return null;
  
  const presets = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'last7', label: 'Last 7 days' },
    { id: 'last4weeks', label: 'Last 4 weeks' },
    { id: 'last6months', label: 'Last 6 months' },
    { id: 'last12months', label: 'Last 12 months' },
    { id: 'wtd', label: 'Week to date' },
    { id: 'mtd', label: 'Month to date' },
    { id: 'qtd', label: 'Quarter to date' },
    { id: 'ytd', label: 'Year to date' },
  ];
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Monday-first grid: shift Sun(0) to the end so Mon is column 0.
    const startingDay = (firstDay.getDay() + 6) % 7;

    const days = [];
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  
  const isInRange = (date) => {
    if (!localStart || !localEnd || !date) return false;
    return date >= localStart && date <= localEnd;
  };
  
  const isStartDate = (date) => {
    if (!localStart || !date) return false;
    return date.toDateString() === localStart.toDateString();
  };
  
  const isEndDate = (date) => {
    if (!localEnd || !date) return false;
    return date.toDateString() === localEnd.toDateString();
  };
  
  const handleDateClick = (date) => {
    if (!date) return;

    if (selecting === 'start') {
      setLocalStart(date);
      setLocalEnd(null);
      setStartInput(formatDateInput(date));
      setEndInput('');
      setSelecting('end');
    } else {
      if (date < localStart) {
        setLocalStart(date);
        setLocalEnd(localStart);
        setStartInput(formatDateInput(date));
        setEndInput(formatDateInput(localStart));
      } else {
        setLocalEnd(date);
        setEndInput(formatDateInput(date));
      }
      setSelecting('start');
    }
    onPresetChange(null); // Clear preset when manually selecting
  };
  
  const handlePresetClick = (presetId) => {
    onPresetChange(presetId);
    // Set dates based on preset
    const now = new Date();
    let start, end;

    switch (presetId) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        break;
      case 'last7':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        break;
      case 'last4weeks':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 27);
        break;
      case 'last6months':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case 'last12months':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case 'wtd':
        // Week to date - from Monday of current week
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dayOfWeek = (now.getDay() + 6) % 7; // 0 = Monday
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        break;
      case 'mtd':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'qtd':
        // Quarter to date
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterStartMonth, 1);
        break;
      case 'ytd':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
        // All time - from a very old date (e.g., 2015) to today
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(2015, 0, 1);
        break;
      default:
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        start = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    setLocalStart(start);
    setLocalEnd(end);
    setStartInput(formatDateInput(start));
    setEndInput(formatDateInput(end));
    setViewMonth(new Date(end.getFullYear(), end.getMonth(), 1));
    onApply(start, end, presetId);
    onClose();
  };
  
  const handleApply = () => {
    if (localStart && localEnd) {
      onApply(localStart, localEnd);
    }
    onClose();
  };
  
  const prevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };
  
  const days = getDaysInMonth(viewMonth);
  
  return (
    <>
      {/* Backdrop */}
      <div
        className={isMobile ? 'sheet-anim-backdrop' : undefined}
        style={{ position: 'fixed', inset: 0, zIndex: PICKER_BACKDROP_Z, backgroundColor: isMobile ? 'rgba(0,0,0,0.4)' : 'transparent' }}
        onClick={onClose}
      />

      {/* Picker */}
      <div ref={panelRef} className={isMobile ? 'sheet-anim-up' : 'sheet-anim-pop'} style={isMobile ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 40px rgba(0, 0, 0, 0.15)',
        zIndex: PICKER_PANEL_Z,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxHeight: '90vh',
        paddingBottom: 'env(safe-area-inset-bottom)'
      } : {
        position: 'absolute',
        ...(resolvedDirection === 'up' ? { bottom: '100%', marginBottom: '8px' } : { top: '100%', marginTop: '8px' }),
        [align]: 0,
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e5e5',
        zIndex: PICKER_PANEL_Z,
        display: 'flex',
        overflow: 'hidden',
        minWidth: '560px'
      }}>
        {/* Mobile header: title + close, matching the house sheet pattern */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Date range</h3>
            <button
              onClick={onClose}
              style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: '#1a1a1a' }}
            >
              &#x2715;
            </button>
          </div>
        )}

        {/* Presets */}
        <div style={isMobile ? {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '14px 16px',
          borderBottom: '1px solid #e5e5e5'
        } : {
          width: '180px',
          borderRight: '1px solid #e5e5e5',
          padding: '12px',
          backgroundColor: '#fafafa'
        }}>
          {!isMobile && (
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 8px' }}>
              Presets
            </p>
          )}
          {presets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetClick(preset.id)}
              style={isMobile ? {
                padding: '8px 14px',
                border: selectedPreset === preset.id ? 'none' : '1px solid #e5e5e5',
                borderRadius: '999px',
                backgroundColor: selectedPreset === preset.id ? '#29282D' : 'white',
                color: selectedPreset === preset.id ? 'white' : '#4b5563',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer'
              } : {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: selectedPreset === preset.id ? '#007EA0' : 'transparent',
                color: selectedPreset === preset.id ? 'white' : '#4b5563',
                fontSize: '13px',
                textAlign: 'left',
                cursor: 'pointer',
                marginBottom: '2px'
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Calendar */}
        <div style={{ padding: '16px', flex: 1, overflowY: isMobile ? 'auto' : 'visible' }}>
          {/* Month Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <button
              onClick={prevMonth}
              style={{
                padding: '8px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderRadius: '6px',
                color: '#737373'
              }}
            >
              <ChevronLeftIcon size={20} />
            </button>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
              {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              onClick={nextMonth}
              style={{
                padding: '8px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderRadius: '6px',
                color: '#737373'
              }}
            >
              <ChevronRightIcon size={20} />
            </button>
          </div>

          {/* Day Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
            {dayNames.map((day) => (
              <div key={day} style={{
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: '600',
                color: '#9ca3af',
                padding: '4px'
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {days.map((date, idx) => {
              if (!date) {
                return <div key={`empty-${idx}`} style={{ padding: '8px' }} />;
              }

              const inRange = isInRange(date);
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              const isToday = date.toDateString() === new Date().toDateString();

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => handleDateClick(date)}
                  style={{
                    padding: isMobile ? '10px 8px' : '8px',
                    border: 'none',
                    borderRadius: isStart ? '6px 0 0 6px' : isEnd ? '0 6px 6px 0' : '0',
                    backgroundColor: isStart || isEnd ? '#007EA0' : inRange ? '#e0f2fe' : 'transparent',
                    color: isStart || isEnd ? 'white' : inRange ? '#0369a1' : isToday ? '#007EA0' : '#1a1a1a',
                    fontSize: '13px',
                    fontWeight: isToday || isStart || isEnd ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.1s'
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Selected Range Display */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e5e5'
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
              <input
                type="text"
                value={startInput}
                placeholder="Start date"
                onChange={(e) => setStartInput(e.target.value)}
                onBlur={(e) => {
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) {
                    setLocalStart(parsed);
                    setStartInput(formatDateInput(parsed));
                    setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
                    if (localEnd && parsed > localEnd) {
                      setLocalEnd(null);
                      setEndInput('');
                    }
                    onPresetChange(null);
                  } else if (localStart) {
                    setStartInput(formatDateInput(localStart));
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid transparent',
                  outline: 'none',
                  width: '120px',
                  color: localStart ? '#1a1a1a' : '#9ca3af'
                }}
                onFocus={(e) => { e.target.style.borderColor = '#007EA0'; e.target.select(); }}
                onBlurCapture={(e) => { e.target.style.borderColor = 'transparent'; }}
              />
              <span style={{ color: '#9ca3af', flexShrink: 0 }}>→</span>
              <input
                type="text"
                value={endInput}
                placeholder="End date"
                onChange={(e) => setEndInput(e.target.value)}
                onBlur={(e) => {
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) {
                    if (localStart && parsed < localStart) {
                      setLocalStart(parsed);
                      setStartInput(formatDateInput(parsed));
                    }
                    setLocalEnd(parsed);
                    setEndInput(formatDateInput(parsed));
                    setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
                    onPresetChange(null);
                  } else if (localEnd) {
                    setEndInput(formatDateInput(localEnd));
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid transparent',
                  outline: 'none',
                  width: '120px',
                  color: localEnd ? '#1a1a1a' : '#9ca3af'
                }}
                onFocus={(e) => { e.target.style.borderColor = '#007EA0'; e.target.select(); }}
                onBlurCapture={(e) => { e.target.style.borderColor = 'transparent'; }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
              <button
                onClick={isMobile ? () => handlePresetClick('today') : onClose}
                style={{
                  padding: isMobile ? '14px' : '8px 16px',
                  border: '1px solid #e5e5e5',
                  borderRadius: isMobile ? '10px' : '8px',
                  backgroundColor: 'white',
                  fontSize: isMobile ? '15px' : '13px',
                  fontWeight: '500',
                  color: '#737373',
                  cursor: 'pointer',
                  flex: isMobile ? 1 : 'none'
                }}
              >
                {isMobile ? 'Reset' : 'Cancel'}
              </button>
              <button
                onClick={handleApply}
                disabled={!localStart || !localEnd}
                style={{
                  padding: isMobile ? '14px' : '8px 16px',
                  border: 'none',
                  borderRadius: isMobile ? '10px' : '8px',
                  backgroundColor: localStart && localEnd ? (isMobile ? '#29282D' : '#007EA0') : '#e5e5e5',
                  fontSize: isMobile ? '15px' : '13px',
                  fontWeight: isMobile ? '600' : '500',
                  color: localStart && localEnd ? 'white' : '#9ca3af',
                  cursor: localStart && localEnd ? 'pointer' : 'not-allowed',
                  flex: isMobile ? 1 : 'none'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DateRangePicker;
